import { ColumnDef } from '@tanstack/react-table';
import { Badge } from '../../components/Badge';
import { NotificationStateIcon } from '../../components/notifications';
import { AlarmTriggerState, NotificationEvent } from '../../gql/generated';
import {
  formatChannelNames,
  formatNotificationDate,
  notificationStateLabel,
  notificationStateVariant,
} from '../../utils/notificationFormatters';
import {
  notificationAlarmLabel,
  notificationSignalLabel,
  notificationThresholdLabel,
} from '../../utils/notificationMembers';

export function createNotificationColumns(): ColumnDef<NotificationEvent>[] {
  return [
    {
      id: 'stateIcon',
      header: '',
      accessorKey: 'alarmTriggerState',
      cell: ({ getValue }) => {
        const state =
          getValue<AlarmTriggerState | null>() ?? AlarmTriggerState.Triggered;
        return (
          <div className='flex items-center justify-center'>
            <NotificationStateIcon state={state} />
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
      enableResizing: false,
      size: 40,
      minSize: 40,
    },
    {
      id: 'machineName',
      header: 'Display name',
      accessorKey: 'machineName',
      cell: ({ getValue }) => (
        <span className='size-p2 text-primary block truncate font-medium'>
          {getValue<string>() || '—'}
        </span>
      ),
      enableSorting: true,
      enableResizing: true,
      size: 180,
    },
    {
      id: 'signal',
      header: 'Signal',
      accessorKey: 'signal',
      cell: ({ row }) => (
        <span className='size-p2 text-primary block truncate'>
          {notificationSignalLabel(row.original) || '—'}
        </span>
      ),
      enableSorting: false,
      enableResizing: true,
      size: 160,
    },
    {
      id: 'timeUtc',
      header: 'Alarm sent',
      accessorKey: 'timeUtc',
      cell: ({ getValue }) => (
        <span className='size-p2 text-primary'>
          {formatNotificationDate(getValue<string>())}
        </span>
      ),
      enableResizing: true,
      size: 170,
    },
    {
      id: 'threshold',
      header: 'Threshold',
      cell: ({ row }) => {
        const thresholdText = notificationThresholdLabel(row.original);
        if (!thresholdText) {
          return <span className='text-tertiary size-p2'>—</span>;
        }

        return (
          <div className='size-p2 truncate' title={thresholdText}>
            {thresholdText}
          </div>
        );
      },
      enableSorting: false,
      enableResizing: true,
      size: 120,
    },
    {
      id: 'alarmTriggerState',
      header: 'Status',
      accessorKey: 'alarmTriggerState',
      cell: ({ getValue }) => {
        const state =
          getValue<AlarmTriggerState | null>() ?? AlarmTriggerState.Triggered;
        return (
          <Badge variant={notificationStateVariant[state]}>
            {notificationStateLabel[state]}
          </Badge>
        );
      },
      enableResizing: true,
      size: 140,
    },
    {
      id: 'alarmName',
      header: 'Pulse alert',
      accessorKey: 'alarmName',
      cell: ({ row }) => (
        <span className='size-p2 text-primary block truncate'>
          {notificationAlarmLabel(row.original) || '—'}
        </span>
      ),
      enableSorting: true,
      enableResizing: true,
      size: 180,
    },
    {
      id: 'subject',
      header: 'Subject',
      accessorKey: 'subject',
      cell: ({ getValue }) => (
        <span className='size-p2 text-primary block truncate'>
          {getValue<string>() || '—'}
        </span>
      ),
      enableResizing: true,
      size: 240,
    },
    {
      id: 'channels',
      header: 'Channels',
      cell: ({ row }) => {
        const deliveries = row.original.notificationDeliveries || [];
        if (deliveries.length === 0) {
          return <span className='text-tertiary size-p2'>—</span>;
        }

        return (
          <span className='size-p2 text-secondary'>
            {formatChannelNames(deliveries)}
          </span>
        );
      },
      enableResizing: true,
      size: 200,
    },
    {
      id: 'actions',
      header: '',
      cell: () => null,
      enableSorting: false,
      enableHiding: false,
      enableResizing: false,
      enablePinning: true,
      size: 58,
      minSize: 58,
    },
  ];
}
